// @flow
import {fromJS, Set, List, Map} from 'immutable';
import moment from 'moment';
import {setUpStore, Store} from 'client/framework';
import appStore from 'client/AppStore';
import {type QueryBuilder, resource} from '@albert-io/json-api-framework/request/builder';
import usageReportsActions from './UsageReports.actions';
import SelectableDateRangeStore from 'generic/SelectableDateRange/SelectableDateRange.store';
import {type GenericModel} from 'resources/Generic.model';

class UsageReportsStore extends Store {
  constructor(name: string) {
    super(name);

    this.selectableDateRangeStore = setUpStore(
      SelectableDateRangeStore,
      'UsageReportsSelectableDateRangeStore'
    );
    this.setInitialData(
      fromJS({
        classroomStatuses: ['active'],
        areSubjectsExpanded: false,
        areTeachersExpanded: false
      })
    );

    this.writeData('classroomStatuses', Set(this.initialData.get('classroomStatuses')));
    this.handle(usageReportsActions.TOGGLE_CLASSROOM_STATUS, this._toggleClassroomStatus);
    this.handle(
      usageReportsActions.TOGGLE_EXPAND_SUBJECTS,
      this.toggleProperty('areSubjectsExpanded')
    );
    this.handle(
      usageReportsActions.TOGGLE_EXPAND_TEACHERS,
      this.toggleProperty('areTeachersExpanded')
    );
    this.handle(usageReportsActions.RESET_STORE, this.resetStore);
  }

  _toggleClassroomStatus(status: string) {
    const currentStatus = this.getStatus();
    const updatedStatus = currentStatus.includes(status)
      ? currentStatus.delete(status)
      : currentStatus.add(status);

    this.writeData('classroomStatuses', updatedStatus);
  }

  /**
   * @todo
   * for https://github.com/albert-io/project-management/issues/1437, we had to move the
   * teacher index classroom_v1 status filter into the "top level" of the query and out of a
   * logical filter -- it is unclear if that is an issue for the other tabs in the table.
   */
  _addStatusFilterToQuery(query: QueryBuilder, queryType: string = ''): QueryBuilder {
    let statuses = this.getStatus();

    if (statuses.has('active') && statuses.has('archived')) {
      return query;
    }

    statuses = statuses.map((status) => {
      if (queryType === 'classrooms') {
        return {status};
      }

      return {classrooms_v1: {status}};
    });

    if (queryType === 'students' || queryType === 'classrooms') {
      return query.customQuery({
        filter: statuses.first()
      });
    }

    return query.filter({
      any_of: statuses.toJS()
    });
  }

  getStatus(): Set<*> {
    return this.readData('classroomStatuses');
  }

  getBaseTeacherQuery(): QueryBuilder {
    const statuses = this.getStatus();

    const query = resource('teachers_v1')
      .mandarkEndpoint(['teachers_v1'])
      .customQuery({
        with_meta: 'teacher_v1'
      })
      .filter({
        school_personnel_v1: {
          status: 'approved',
          school_v5: appStore.routerProps.params.schoolId
        },
        classrooms_v1: {
          school_v5: appStore.routerProps.params.schoolId
        }
      })
      // only provide a status filter if we have active and archived applied
      .filter(
        {
          classrooms_v1: {
            status: statuses.first()
          }
        },
        statuses.has('active') && statuses.has('archived') === false
      );

    return query;
  }

  getTeachersContext(): Object {
    return {
      start_date: this.getStartDate(),
      end_date: this.getEndDate(),
      classroom: {
        statuses: this.getStatus().join(',')
      },
      subject: {
        breakdown: this.areSubjectsExpanded()
      },
      school: {
        id: appStore.routerProps.params.schoolId
      }
    };
  }

  getTeachersQuery(): QueryBuilder {
    const context = this.getTeachersContext();
    return this.getBaseTeacherQuery().meta({context});
  }

  getTeachersCSVQuery = (csvHeaders: string): QueryBuilder => {
    const context = Object.assign(this.getTeachersContext(), {csv_headers: csvHeaders});
    return this.getBaseTeacherQuery()
      .customHeader({Accept: 'text/csv'})
      .csv({
        context: {
          school_administrator: {
            dashboard: true
          }
        }
      })
      .csv({context})
      .done();
  };

  getTeacherSearchFilters(query: QueryBuilder, term: string): QueryBuilder {
    return query.filter({
      all_of: [
        {
          any_of: [
            {
              classrooms_v1: {
                subjects_v2: {
                  name: {
                    case_insensitive_substring: term
                  }
                }
              }
            },
            {
              first_name: {
                case_insensitive_substring: term
              }
            },
            {
              last_name: {
                case_insensitive_substring: term
              }
            },
            {
              email: {
                case_insensitive_substring: term
              }
            }
          ]
        }
      ]
    });
  }

  getBaseStudentQuery(): QueryBuilder {
    const query = resource('students_v2')
      .mandarkEndpoint(['students_v2'])
      .customQuery({
        with_meta: 'student_v2'
      })
      .filter({
        classrooms_v1: {
          teachers_v1: {
            school_personnel_v1: {
              status: 'approved',
              school_v5: appStore.routerProps.params.schoolId
            }
          },
          school_v5: appStore.routerProps.params.schoolId
        }
      });

    return this._addStatusFilterToQuery(query, 'students');
  }

  getStudentsContext(): Object {
    return {
      start_date: this.getStartDate(),
      end_date: this.getEndDate(),
      classroom: {
        statuses: this.getStatus().join(',')
      },
      subject: {
        breakdown: this.areSubjectsExpanded()
      },
      school: {
        id: appStore.routerProps.params.schoolId
      }
    };
  }

  getStudentsQuery(): QueryBuilder {
    const context = this.getStudentsContext();
    return this.getBaseStudentQuery().meta({context});
  }

  getStudentsCSVQuery = (csvHeaders: string): QueryBuilder => {
    const context = Object.assign(this.getStudentsContext(), {csv_headers: csvHeaders});
    return this.getBaseStudentQuery()
      .customHeader({Accept: 'text/csv'})
      .csv({
        context: {
          school_administrator: {
            dashboard: true
          }
        }
      })
      .csv({context})
      .done();
  };

  getStudentSearchFilters(query: QueryBuilder, term: string): QueryBuilder {
    return query.filter({
      all_of: [
        {
          any_of: [
            {
              classrooms_v1: {
                subjects_v2: {
                  name: {
                    case_insensitive_substring: term
                  }
                }
              }
            },
            {
              first_name: {
                case_insensitive_substring: term
              }
            },
            {
              last_name: {
                case_insensitive_substring: term
              }
            },
            {
              email: {
                case_insensitive_substring: term
              }
            }
          ]
        }
      ]
    });
  }

  getBaseClassroomQuery(): QueryBuilder {
    const statuses = this.getStatus();
    const isArchivedOnly = statuses.has('archived') && statuses.has('active') === false;
    const query = resource('classrooms_v1')
      .mandarkEndpoint(['schools_v4', appStore.routerProps.params.schoolId, 'classrooms_v1'])
      .customQuery({
        with_meta: 'classroom_v1,teacher_v1'
      })
      .include('teachers_v1,subjects_v2')
      .filter({
        teachers_v1: {
          school_personnel_v1: {
            status: 'approved',
            school_v5: appStore.routerProps.params.schoolId
          }
        }
      })
      .filter(
        {
          end_date: {
            greater_than_inclusive: this.getStartDate()
          }
        },
        !this.isFullYear() && isArchivedOnly
      )
      .filter(
        {
          begin_date: {
            less_than_inclusive: this.getEndDate()
          },
          auto_archive_date: {
            greater_than_inclusive: this.getStartDate()
          }
        },
        this.isFullYear() && isArchivedOnly
      );

    return this._addStatusFilterToQuery(query, 'classrooms');
  }

  getClassroomsContext(): Object {
    return {
      start_date: this.getStartDate(),
      end_date: this.getEndDate(),
      classroom: {
        statuses: this.getStatus().join(',')
      },
      subject: {
        breakdown: this.areSubjectsExpanded()
      },
      school: {
        id: appStore.routerProps.params.schoolId
      }
    };
  }

  getClassroomsQuery(): QueryBuilder {
    const context = this.getClassroomsContext();
    return this.getBaseClassroomQuery().meta({context});
  }

  getClassroomsCSVQuery = (csvHeaders: string): QueryBuilder => {
    const context = Object.assign(this.getClassroomsContext(), {csv_headers: csvHeaders});
    return this.getBaseClassroomQuery()
      .customHeader({Accept: 'text/csv'})
      .csv({
        context: {
          school_administrator: {
            dashboard: true
          }
        }
      })
      .csv({context})
      .done();
  };

  getClassroomSearchFilters(query: QueryBuilder, term: string): QueryBuilder {
    return query.filter({
      any_of: [
        {
          teachers_v1: {
            first_name: {
              case_insensitive_substring: term
            }
          }
        },
        {
          teachers_v1: {
            last_name: {
              case_insensitive_substring: term
            }
          }
        },
        {
          teachers_v1: {
            email: {
              case_insensitive_substring: term
            }
          }
        },
        {
          subjects_v2: {
            name: {
              case_insensitive_substring: term
            }
          }
        },
        {
          name: {
            case_insensitive_substring: term
          }
        }
      ]
    });
  }

  getBaseSubjectQuery(): QueryBuilder {
    const query = resource('subjects_v2')
      .mandarkEndpoint(['subjects_v2'])
      .customQuery({
        with_meta: 'subject_v2'
      })
      .filter({
        'meta.count_of_school_classrooms': {
          not: 0
        }
      });

    return this._addStatusFilterToQuery(query);
  }

  getSubjectsContext(): Object {
    return {
      start_date: this.getStartDate(),
      end_date: this.getEndDate(),
      classroom: {
        statuses: this.getStatus().join(',')
      },
      school: {
        id: appStore.routerProps.params.schoolId
      }
    };
  }

  getSubjectsQuery(): QueryBuilder {
    const context = this.getSubjectsContext();
    return this.getBaseSubjectQuery().meta({context});
  }

  getSubjectsCSVQuery = (csvHeaders: string): QueryBuilder => {
    const context = Object.assign(this.getSubjectsContext(), {csv_headers: csvHeaders});
    return this.getBaseSubjectQuery()
      .customHeader({Accept: 'text/csv'})
      .csv({
        context: {
          school_administrator: {
            dashboard: true
          }
        }
      })
      .csv({context})
      .done();
  };

  getSubjectSearchFilters(query: QueryBuilder, term: string): QueryBuilder {
    return query.filter({
      all_of: [
        {
          name: {
            case_insensitive_substring: term
          }
        }
      ]
    });
  }

  /**
   * When subjects are expanded, every table line item with multiple subjects is broken up into one line item
   * for each subject.
   *
   * Technical Debt:
   * Unfortunately, the filtering we are doing against the  resource index that builds this table is NOT
   * applied to the returned `school_subject_breakdown` meta structure. Because we need to use this meta to construct a
   * full table when expand subjects is engaged, we have to apply the same filters we're using in the index
   * query to these expanded subject entires.
   *
   * Below, we create a list of data objects each with ONE subject associated. This allows us to do filtering on that single
   * subject.
   */
  expandSubjectsForData(data: List<GenericModel>): List<GenericModel> {
    let dataList = List();
    data.forEach((datum) => {
      const subjectBreakdown = datum.getMeta().getSchoolSubjectBreakdown();
      const subjects = subjectBreakdown.reduce((acc, value, key) => {
        return acc.push({
          name: value.get('subject_name'),
          id: key
        });
      }, List());

      // If something has no subject it still needs to be in the table.
      if (subjects.isEmpty()) {
        const dataObject = {
          data: datum,
          subject: ''
        };

        dataList = dataList.push(dataObject);
      } else {
        subjects.forEach((subject) => {
          // Because I can't set a subject on a resource and read it later, this is a workaround
          const dataObject = {
            data: datum.setIn(
              ['meta', 'school_subject_breakdown'],
              subjectBreakdown.get(subject.id)
            ),
            subject: subject.name
          };
          dataList = dataList.push(dataObject);
        });
      }
    });

    return dataList;
  }

  mapExpandedData(expandedData: List<{}>): List<GenericModel> {
    return expandedData.map((dataObject) => {
      return dataObject.data;
    });
  }

  getTeachersExpandedSubjectsMutator(searchTerm: string): (data: List<{}>) => List<GenericModel> {
    const normalizedSearchTerm = searchTerm.toLowerCase();
    return (data: List<*>) => {
      const teachers = this.expandSubjectsForData(data);

      if (!normalizedSearchTerm) {
        return this.mapExpandedData(teachers);
      }

      const filteredTeachers = teachers.filter((teacherObject) => {
        const teacher = teacherObject.data;
        const teacherFirst = teacher.getFirstName().toLowerCase();
        const teacherLast = teacher.getLastName().toLowerCase();
        const teacherEmail = teacher.getEmail().toLowerCase();
        const subjectName = teacherObject.subject.toLowerCase();

        return [teacherFirst, teacherLast, teacherEmail, subjectName].some((value) => {
          return value.includes(normalizedSearchTerm);
        });
      });

      return this.mapExpandedData(filteredTeachers);
    };
  }

  getStudentsExpandedSubjectsMutator(searchTerm: String): (data: List<{}>) => List<GenericModel> {
    const normalizedSearchTerm = searchTerm.toLowerCase();
    return (data: List<*>) => {
      const students = this.expandSubjectsForData(data);

      if (!normalizedSearchTerm) {
        return this.mapExpandedData(students);
      }

      const filteredStudents = students.filter((studentObject) => {
        const student = studentObject.data;
        const studentFirst = student.getFirstName().toLowerCase();
        const studentLast = student.getLastName().toLowerCase();
        const studentEmail = student.getEmail().toLowerCase();
        const subjectName = studentObject.subject ? studentObject.subject.toLowerCase() : '';

        return [studentFirst, studentLast, studentEmail, subjectName].some((value) => {
          return value.includes(normalizedSearchTerm);
        });
      });

      return this.mapExpandedData(filteredStudents);
    };
  }

  getClassesExpandSubjectsMutator(searchTerm: String): (data: List<{}>) => List<GenericModel> {
    const normalizedSearchTerm = searchTerm.toLowerCase();
    return (data: List<*>) => {
      const classrooms = this.expandSubjectsForData(data);

      if (!normalizedSearchTerm) {
        return this.mapExpandedData(classrooms);
      }

      const filteredClassrooms = classrooms.filter((classroomObject) => {
        const classroom = classroomObject.data;
        const teacherFirst = classroom
          .getTeachers()
          .first()
          .getFirstName()
          .toLowerCase();
        const teacherLast = classroom
          .getTeachers()
          .first()
          .getLastName()
          .toLowerCase();
        const teacherEmail = classroom
          .getTeachers()
          .first()
          .getEmail()
          .toLowerCase();
        const className = classroom.getName().toLowerCase();
        const subjectName = classroomObject.subject.toLowerCase();

        return [teacherFirst, teacherLast, teacherEmail, className, subjectName].some((value) => {
          return value.includes(normalizedSearchTerm);
        });
      });

      return this.mapExpandedData(filteredClassrooms);
    };
  }

  getDateRangeDropdownStore(): Map<string, *> | null {
    return this.selectableDateRangeStore.getSimpleDropdownStore();
  }

  getStartDate(): number {
    const startDate = this.getDateRangeDropdownStore()
      .getSelectedItem()
      .get('start_date', 0);
    return moment(startDate)
      .startOf('day')
      .valueOf();
  }

  getEndDate(): number {
    const endDate = this.getDateRangeDropdownStore()
      .getSelectedItem()
      .get('end_date', moment());
    return moment(endDate)
      .endOf('day')
      .valueOf();
  }

  getSelectableDateRangeStoreName(): string {
    return this.selectableDateRangeStore.getName();
  }

  isFullYear() {
    const startDate = this.getDateRangeDropdownStore()
      .getSelectedItem()
      .get('start_date', 0);
    const startDateMoment = moment(startDate).startOf('day');

    const endDate = this.getDateRangeDropdownStore()
      .getSelectedItem()
      .get('end_date', moment());
    const endDateMoment = moment(endDate).endOf('day');

    return Math.round(endDateMoment.diff(startDateMoment, 'years', true)) >= 1;
  }

  isClassroomStatusDisabled(status: string): boolean {
    const statuses = this.getStatus();
    return statuses.size === 1 && statuses.includes(status);
  }

  isClassroomStatusToggled(status: string): boolean {
    return this.getStatus().includes(status);
  }

  areSubjectsExpanded(): boolean {
    return this.readData('areSubjectsExpanded');
  }

  areTeachersExpanded(): boolean {
    return this.readData('areTeachersExpanded');
  }
}

export default new UsageReportsStore('UsageReportsStore');
